import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('change', this.#handleVariantChange, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelection, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const selectedMediaId = resource?.featured_media?.id;

        // A normal variant selection does not need to rebuild the gallery.
        // Keeping the existing gallery mounted prevents layout shifts and
        // duplicate image changes after the immediate radio-change handler.
        if (!detail.newProduct) {
          if (selectedMediaId) this.#selectMediaById(selectedMediaId);
          return;
        }

        this.replaceWith(newMediaGallery);

        if (!selectedMediaId) return;

        const replacementGallery = /** @type {MediaGallery} */ (newMediaGallery);
        const slides = Array.from(replacementGallery.querySelectorAll('slideshow-slide[data-media-id]'));
        const selectedIndex = slides.findIndex((slide) => slide.getAttribute('data-media-id') === String(selectedMediaId));

        if (selectedIndex >= 0) {
          replacementGallery.slideshow?.select(selectedIndex, undefined, { animate: false });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Moves the gallery immediately when a colour card is selected, before the
   * section response finishes loading.
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const mediaId = event.target.dataset.featuredMediaId;
    if (!mediaId) return;

    const selectedValue = event.target.closest('fieldset')?.querySelector('.variant-option__swatch-value');
    if (selectedValue) selectedValue.textContent = event.target.value;

    this.#selectMediaById(mediaId, event);
  };

  /**
   * Keeps the colour picker in sync when a customer chooses or swipes to a
   * gallery image that belongs to a colour variant.
   * @param {CustomEvent} event
   */
  #handleSlideSelection = (event) => {
    if (!event.detail?.userInitiated) return;

    const mediaId = event.detail.slide?.getAttribute('data-media-id');
    if (!mediaId) return;

    const target = this.closest('.shopify-section, dialog');
    const radio = target?.querySelector(`variant-picker input[type="radio"][data-featured-media-id="${CSS.escape(mediaId)}"]`);

    if (!(radio instanceof HTMLInputElement) || radio.checked || radio.getAttribute('aria-disabled') === 'true') return;

    radio.click();
  };

  /**
   * @param {string|number} mediaId
   * @param {Event} [event]
   */
  #selectMediaById(mediaId, event) {
    const slides = Array.from(this.querySelectorAll('slideshow-slide[data-media-id]'));
    const selectedIndex = slides.findIndex((slide) => slide.getAttribute('data-media-id') === String(mediaId));

    if (selectedIndex >= 0) {
      this.slideshow?.select(selectedIndex, event, { animate: false });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
